import { Component, ViewChild, Input, OnDestroy, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, Injector } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { FormType, FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { Comment, CommentResource } from 'src/app/controls/comments-and-attachments/services/comment';
import { CommentService } from 'src/app/controls/comments-and-attachments/services/comments.service';
import { FileV2Service } from 'src/app/services/file/file-v2.service';
import { YesNoDialogComponent } from '../yes-no-dialog/yes-no-dialog.component';
import { MatFileUploadService } from 'src/app/modules/file-upload/file-upload-module';
import { MatFileV2UploadComponent } from 'src/app/modules/file-upload/lib/mat-file-v2-upload/mat-file-v2-upload.component';
import { BaseComponent } from 'src/app/common/base/base.component';
import { CommentLoadBySerialNo, CommentRefreshByID } from './store/comments.action';

@Component({
  selector: 'comments-and-attachments',
  templateUrl: './comments-and-attachments.component.html',
  styleUrls: ['./comments-and-attachments.component.scss']
})
export class CommentsAndAttachmentsComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() currentDocument?: any = null;
  @Input() SerialNo?: string;
  @Input() Title = 'Comments and Attachments';
  @Input() ShowFiles = true;
  @Input() disabled?: boolean;
  @Input() headerClass = 'h3';

  @Output() changed = new EventEmitter<any>();

  formTypes = FormType;

  comment?: string | null;

  comments?: CommentResource[];
  commentsFiltered?: CommentResource[];
  comments$!: Observable<CommentResource[]>;
  commentsSubs!: Subscription;

  loading = false;
  loadingFile = false;
  save_files_database = '';
  commentFiles?: any[];
  typeDocument = '';
  formType!: string;
  docClosed?: boolean;

  @ViewChild(MatFileV2UploadComponent) private matFilev2Upload!: MatFileV2UploadComponent;
  serialNo?: string;

  AttachmentsDisabled?: boolean;

  constructor(
    private commentServ: CommentService,
    private file2Service: FileV2Service,
    private uploadServ: MatFileUploadService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loading = false;

    if (this.currentDocument) {
      this.docClosed = this.currentDocument.status === FormStatusEnum.Closed || this.currentDocument.status === FormStatusEnum.Canceled;
    }

    if (this.serialNo != this.currentDocument?.serialNo) {
      this.serialNo = this.currentDocument?.serialNo;

      this.refresh();
    }
    if (!this.serialNo) this.serialNo = this.SerialNo;
    this.formType = this.currentDocument?.documentType?.code ?? this.serialNo?.split('-')[0];
  }

  /**
   * Shows the current form's comments from the store and requests them from the API the first time the form is opened.
   * Replaces any previous store subscription; later SignalR comment changes update the list through the same subscription.
   */
  loadComments() {
    setTimeout(() => {
      this.loading = true;

      this.comments = [];
      // 6963: re-subscribing on every refresh used to leak subscriptions
      this.commentsSubs?.unsubscribe();
      const serialNo = this.serialNo;
      if (!serialNo) {
        this.loading = false;
        return;
      }
      this.comments$ = this.store.select(state => state.Comments.data);
      // 6963: the store only holds opened forms now, so an empty list is valid (no comments yet or last one deleted)
      this.commentsSubs = this.comments$.subscribe(data => {
        this.comments = (data ?? []).filter(x => x.serialNo == serialNo).sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
      });
      // Loads this form's comments once per session; completes immediately when they are already in the store
      this.store.dispatch(new CommentLoadBySerialNo(serialNo)).subscribe(() => this.loading = false);
    }, 100);
  }

  override ngOnDestroy(): void {
    this.commentsSubs?.unsubscribe();
  }

  initForm() {
    // this.buildForm();
  }

  refresh() {
    setTimeout(() => {
      this.loading = true;
      this.AttachmentsDisabled = !this.currentDocument || this.currentDocument.status === FormStatusEnum.Closed;
      this.serialNo = this.currentDocument?.serialNo ? this.currentDocument?.serialNo : this.SerialNo;
      if (this.matFilev2Upload) {
        this.matFilev2Upload.uploadId = this.serialNo ?? '-';
      }
      this.loadComments();
    }, 100);
  }

  clear() {
    this.comments = [];
    this.comment = null;
  }

  create() {
    this.currentDocument = null;
    this.clear();
  }

  addComment() {
    this.loading = true;
    const newComment: Comment = {
      serialNo: this.serialNo,
      commentText: this.comment,
    };
    this.comment = '';
    this.commentServ.Create(newComment).toPromise().then(async res => {
      if (this.matFilev2Upload.selectedFiles?.length && res?.id) {
        this.loading = true;
        await this.uploadAttachments(res.id);
      }
      this.alert.success(this.getMessage('CommentAdded').description);
      this.changed.emit(true);
      this.refresh();
      this.loading = false;
    },
      error => {
        this.alert.error(error);
      });
  }

  uploadAttachments(id: number) {
    return new Promise((resolve, reject) => {
      this.matFilev2Upload
        .uploadFiles(id)
        .then(() => {
          resolve(true);
        })
        .catch(error => {
          console.log(error);
          reject(false);
        })
        .finally(() => {
          this.loading = false;
          this.store.dispatch(new CommentRefreshByID(id));
        });
    });
  }

  commentChanged(e: any) {
    this.comment = e;
  }

  DeleteComment(c: CommentResource) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('Delete_Comments_Confirmation').description,
        icon: 'stop'
      }
    });

    confirm.afterClosed().toPromise().then(data => {
      if (data && c.id) {
        this.loading = true;
        this.commentServ.Delete(c.id).toPromise().then((res) => {
          // 6963: the store subscription no longer clears the loading flag, the SignalR Delete removes the comment from the list
          this.loading = false;
          this.alert.success(this.getMessage('CommentDeleted').description);
          this.changed.emit(true);
        },
          (error) => {
            this.loading = false;
            console.error(error);
          });
      }
    },
      (error) => console.error(error));
  }

  deleteCommentFile(idFileDelete: string) {
    this.uploadServ.deleteFile(idFileDelete).subscribe(
      () => console.log(idFileDelete.toString()),
      (error) => console.error(error));
  }

  deleteCommentFileServer(idFileDelete: number) {
    this.file2Service.delete(idFileDelete).subscribe(
      () => console.log(idFileDelete.toString()),
      (error) => console.error(error));
  }

  getFloatLabel(): string {
    return 'auto';
  }

}
